import React, { useEffect } from 'react'
import { View, StyleProp, ViewStyle } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import moment from 'moment/moment'
import { StyleService, useStyleSheet } from '@src/style/service'
import { referralConfigSelector, userSelector } from '@src/selectors/app'
import { useIsDarkMode } from '@config/theme.ts'
import { uiFlagsSelector } from '@selectors/uiFlags.ts'
import { TouchableSection } from '@components'
import GiftIcon from '@assets/svgs/giftIcon'
import { UiStateNames } from '@src/types.ts'
import Storage from '@utils/storage.ts'
import App from '@config/app.ts'
import { Analytics, CustomEventTypes } from '@config'

interface ReferralBannerProps {
  style?: StyleProp<ViewStyle>
}

export const ReferralBanner = ({ style }: ReferralBannerProps) => {
  const styles = useStyleSheet(themedStyles)
  const isDarkMode = useIsDarkMode()
  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const uiFlags = useSelector(uiFlagsSelector)
  const navigation = useNavigation()
  const { title: subtitle } = useSelector(referralConfigSelector)
  const bannerShownAtKey = `${Storage.REFERRAL_BANNER_SHOWN_AT_KEY}_${user?.id}`

  useEffect(() => {
    if (!uiFlags.showReferralBanner) {
      return
    }

    if (!Storage.get(bannerShownAtKey)) {
      Storage.set(bannerShownAtKey, moment().toISOString())
    }

    Analytics.track(CustomEventTypes.ReferralBannerViewed)
  }, [uiFlags, bannerShownAtKey])

  if (!uiFlags.showReferralBanner) {
    return null
  }

  const onPress = () => {
    Analytics.track(CustomEventTypes.ReferralBannerClicked)
    navigation.navigate('FriendReferral', { source: 'Drawer' })
  }

  const onDismiss = () => {
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.ReferralBannerDismissed,
        value: true,
      },
    })
    dispatch({
      type: 'uiFlags/updateUiFlags',
      payload: { showReferralBanner: false },
    })
    Analytics.track(CustomEventTypes.ReferralBannerDismissed)
  }

  const bannerShownAt = Storage.get(bannerShownAtKey)

  const dismissibleAfterHours = App.build.alpha ? 1 : 48
  const dismissible =
    bannerShownAt &&
    moment(bannerShownAt).isBefore(moment().subtract(dismissibleAfterHours, 'hours'))

  return (
    <View style={style}>
      <TouchableSection
        title="Refer your friends today"
        subtitle={subtitle}
        onPress={onPress}
        onDismiss={dismissible ? onDismiss : undefined}
        style={[styles.content, isDarkMode && styles.darkContent]}
        subtitleStyle={[styles.subtitle, isDarkMode && styles.darkSubtitle]}
        labelStyle={[styles.title, isDarkMode && styles.darkTitle]}
        accessoryLeft={
          <View style={styles.iconContainer}>
            <GiftIcon height={60} />
          </View>
        }
        showRightArrow
      />
    </View>
  )
}

const themedStyles = StyleService.create({
  title: {
    color: 'theme.marketing.onText',
  },
  darkTitle: {
    color: 'theme.text.primary',
  },
  subtitle: {
    color: 'theme.marketing.subtleText',
    maxWidth: 246,
  },
  darkSubtitle: {
    color: 'theme.text.secondary',
  },
  content: {
    height: 'auto',
    backgroundColor: 'theme.marketing.lightYellow',
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
  },
  darkContent: {
    backgroundColor: 'theme.background.modal',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
})
